import { useEffect, useState, memo, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import UtilsService from '../../Services/UtilsService';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import PromoBanner from '../PromoBanner/PromoBanner';
import AnalyticsService, {
    AnalyticsEvents
} from '../../Services/AnalyticsService';
import { PROMO_BANNER_EXCLUSION_PATHS } from '../../constants/constants';
import { usePromoCode } from '../../hooks/usePromoCode';
import { Coupons, MAP_OF_PROMO_CODES_CONFIG } from '../../constants/promoCodes';
import useReferral from '../../hooks/useReferral';
import NavigateLink from '../Link/NavigateLink';
import { TryForFreeCtaContext } from '../../context/TryForFreeContext';
import { BANNER_CONTENT_COMPONENT_MAP } from './bannerContentRegistry';

const DiscountBanner = ({ path }: { path?: string }) => {
    const { t, i18n } = useTranslation();
    const { promoCode } = usePromoCode();
    const [isCurrentUrlInBannerList, setIsCurrentUrlInBannerList] =
        useState(false);
    const { isReferral } = useReferral();
    const breakpoints = useBreakpoint();

    const tryForFreeCtaContextValue = useContext(TryForFreeCtaContext);

    const shouldShowTryForFreeCTA = tryForFreeCtaContextValue?.isCtaTest;

    const isMobile = !breakpoints.mediumUp;

    let buttonText = '';
    if (shouldShowTryForFreeCTA) {
        buttonText = t('registrationForm.tryForFree');
    } else if (isMobile) {
        buttonText = i18n.exists(`${promoCode}.banner.mobileCtaText`)
            ? t(`${promoCode}.banner.mobileCtaText`)
            : t(`${promoCode}.banner.ctaText`);
    } else {
        buttonText = t(`${promoCode}.banner.ctaText`);
    }

    useEffect(() => {
        setIsCurrentUrlInBannerList(
            UtilsService.isCurrentUrlInList(PROMO_BANNER_EXCLUSION_PATHS)
        );
    }, []);

    const onPromoBannerClose = () => {
        AnalyticsService.track(AnalyticsEvents.welcome20_promo_banner_close);
    };

    const title = (
        <Trans
            t={t}
            i18nKey={`${promoCode}.banner.${
                breakpoints.mediumUp ? 'titleDesktop' : 'titleMobile'
            }`}
        ></Trans>
    );

    const promoConfig = promoCode
        ? MAP_OF_PROMO_CODES_CONFIG[promoCode]
        : undefined;

    const theme = promoConfig?.bannerTheme;
    const tosLink = promoConfig?.tosLink || null;
    const bannerContentType = promoConfig?.bannerContentType;

    const BannerContent = bannerContentType
        ? BANNER_CONTENT_COMPONENT_MAP[bannerContentType]
        : undefined;

    const shouldShowBanner =
        promoCode !== Coupons.nopromo &&
        !isReferral &&
        !isCurrentUrlInBannerList;

    return (
        <PromoBanner
            id={promoCode}
            onClose={onPromoBannerClose}
            userCanDismiss
            shouldShowBanner={shouldShowBanner}
            customClass={`promo-banner promo-banner--${promoCode}`}
            theme={theme}
            path={path}
            isSticky={promoConfig?.isSticky}
        >
            {BannerContent ? (
                <BannerContent promoCode={promoCode} buttonText={buttonText} />
            ) : (
                <div className="promo-banner__text-container">
                    <div className="promo-banner__inner-text-container">
                        <div className="promo-banner__title">{title}</div>

                        {tosLink ? (
                            <NavigateLink
                                className="promo-banner__tos-link"
                                source="promo banner"
                                link={tosLink}
                            >
                                {t(`${promoCode}.banner.tosLinkText`) as string}
                            </NavigateLink>
                        ) : (
                            <RegistrationForm
                                customClass="promo-banner-cta"
                                buttonStyle="invisible"
                                source="promo banner"
                                buttonText={buttonText}
                                couponCode={promoCode}
                                size="x-small"
                            />
                        )}
                    </div>
                </div>
            )}
        </PromoBanner>
    );
};

export default memo(DiscountBanner);
